import { Breakpoints, getWindowWidth } from '@zocdoc/components/src/grid';
import clickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { filter, noop } from 'lodash';
import FacetDropdown from './FacetDropdown';
import { ClearAllOptionsButton, ClearAllOptionsButtonWrapper, FacetChip, FacetChipText } from '../styled-primitives';

export class BaseFacet extends React.Component {
    static propTypes = {
        checkedOptions: PropTypes.instanceOf(Set),
        displayName: PropTypes.string,
        facetId: PropTypes.string,
        facetChipText: PropTypes.string,
        isMulti: PropTypes.bool,
        selectionRequired: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.shape({
            optionId: PropTypes.string,
            displayName: PropTypes.string,
            count: PropTypes.number,
        })),

        appliedOptions: PropTypes.array,
        isActive: PropTypes.bool,
        onApplyFacet: PropTypes.func,
        onCheckOption: PropTypes.func,
        onClearOptions: PropTypes.func,
        onClickFacetChip: PropTypes.func,
        onHideDropdown: PropTypes.func,
        onSelectOption: PropTypes.func,
        onShowDropdown: PropTypes.func,
        onUncheckOption: PropTypes.func,
        updateParameters: PropTypes.func,
    };

    static defaultProps = {
        checkedOptions: new Set(),
        isActive: false,
        isMulti: false,
        onApplyFacet: noop,
        onCheckOption: noop,
        onClearOptions: noop,
        onClickFacetChip: noop,
        onHideDropdown: noop,
        onSelectOption: noop,
        onShowDropdown: noop,
        onUncheckOption: noop,
        selectionRequired: false,
        updateParameters: noop,
    };

    state = {
        showDropdown: false,
    };

    isDesktop() {
        return getWindowWidth() >= Breakpoints.small;
    }

    componentWillUnmount() {
        if (this.state.showDropdown) {
            this.props.onHideDropdown(this);
        }
    }

    render() {
        return (
            <Wrapper className={this.props.className}>
                <FacetChip
                    isActive={this.props.isActive}
                    selectionRequired={this.props.selectionRequired}
                    showDropdown={this.state.showDropdown}
                >
                    <FacetChipText
                        addCloseButtonPadding={!this.props.selectionRequired && this.props.isActive}
                        data-test="facet-chip-button"
                        onClick={this.handleClickFacetChip}
                    >
                        {this.props.facetChipText}
                    </FacetChipText>
                    <ClearAllOptionsButtonWrapper
                        hidden={!this.props.isActive || this.props.selectionRequired}
                        onClick={this.handleClearAllOptions}
                        showDropdown={this.state.showDropdown}
                    >
                        <ClearAllOptionsButton color="currentColor" />
                    </ClearAllOptionsButtonWrapper>
                </FacetChip>
                <FacetDropdown
                    checkedOptions={this.props.checkedOptions}
                    displayName={this.props.displayName}
                    isMulti={this.props.isMulti}
                    onApplyFacet={this.handleApplyFacet}
                    onChangeCheckbox={this.handleChangeCheckbox}
                    onChangeRadio={this.handleChangeRadio}
                    onClickClearButton={this.props.onClearOptions}
                    options={this.props.options}
                    selectionRequired={this.props.selectionRequired}
                    showDropdown={this.state.showDropdown}
                />
            </Wrapper>
        );
    }

    handleClickFacetChip = () => {
        this.props.onClickFacetChip();
        this.setState(({ showDropdown }) => ({
            showDropdown: !showDropdown,
        }));
        if (!this.state.showDropdown) {
            this.props.onShowDropdown(this);
        } else {
            this.props.onHideDropdown(this);
            this.handleApplyFacet();
        }
    }

    handleApplyFacet = () => {
        const { checkedOptions } = this.props;
        const checkedOptionsArray = Array.from(checkedOptions);

        const newState = this.props.facetId === 'procedures'
            ? { procedureId: checkedOptionsArray[0] }
            : { filters: { [this.props.facetId]: checkedOptionsArray } };

        this.props.updateParameters(newState);
        this.exitDropdownAndModal();

        const appliedOptions = filter(this.props.options, ({ optionId }) => checkedOptions.has(optionId));
        this.props.onApplyFacet(appliedOptions);
    }

    handleChangeCheckbox = e => {
        const optionIndex = e.target.getAttribute('data-option-index');
        const { optionId } = this.props.options[optionIndex];

        if (e.target.checked) {
            this.props.onCheckOption(optionId);
        } else {
            this.props.onUncheckOption(optionId);
        }
    }

    handleChangeRadio = e => {
        const optionIndex = e.target.getAttribute('data-option-index');
        const { optionId } = this.props.options[optionIndex];
        this.props.onSelectOption(optionId);
    }

    handleClearAllOptions = () => {
        this.props.onClearOptions();
        this.props.updateParameters({
            filters: {
                [this.props.facetId]: [],
            },
        });
        if (this.state.showDropdown) {
            this.exitDropdownAndModal();
        }
    }

    handleClickOutside() {
        if (this.state.showDropdown && this.isDesktop()) {
            this.handleApplyFacet();
            this.exitDropdownAndModal();
        }
    }

    exitDropdownAndModal = () => {
        this.setState({
            showDropdown: false,
        });
        this.props.onHideDropdown(this);
    }
}

const Wrapper = styled.div`
    display: inline-block;
    position: relative;
`;

export default clickOutside(BaseFacet);
